import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { bool, func, object, string } from "prop-types";
import get from "lodash/get";
import { InputField } from "../../atoms/InputField";
import "./book-seat-section.m.css";
import PlusIcon from "../../../../assets/icons/Plusicon";
import CloseIcon from "../../../../assets/icons/close";
import { hasActiveMembership } from "../../../utils/membership-utils";

const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validateMobile = (mobile) => {
  const mobileRegex = /^[6-9]\d{9}$/;
  return mobileRegex.test(mobile.replace(/\s+/g, ''));
};

export const BookSeatSection = ({
  subscriptionPlans = null,
  onSignIn,
  className = "",
  initRazorPayPayment,
  story = null,
}) => {
  const [error, setError] = useState(null);
  const homePageData = useSelector((state) => get(state, ["qt", "config", "publisher-attributes","homePageData"], {}));
  const member = useSelector((state) => get(state, ["member"], null));
  // Get subscriberMetadata from Redux
  const subscriberMetadata = useSelector((state) => get(state, ["subscriberMetadata"], null));

  // Check if user has active membership using common utility
  const isActiveMember = hasActiveMembership(subscriberMetadata);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    company: "",
    designation: "",
    address: "",
    city: "",
    pincode: "",
    gst: "",
    po: "",
    partners: [],
    privacyConsent: true,
  });

  useEffect(() => {
    setFormData({
      name: get(subscriberMetadata, 'name', '') ||
            `${get(subscriberMetadata, 'first-name', '')} ${get(subscriberMetadata, 'last-name', '')}`.trim(),
      email: get(subscriberMetadata, 'email', '') || get(member, 'email', ''),
      mobile: get(subscriberMetadata, 'phone', '') || get(subscriberMetadata, 'mobile', ''),
      company: get(subscriberMetadata, 'organisation', ''),
      designation: get(subscriberMetadata, 'designation', ''),
      address: "",
      city: "",
      pincode: "",
      gst: "",
      po: "",
      partners: [],
      privacyConsent: true,
    });
  }, [subscriberMetadata]);

  const memberPlan = subscriptionPlans?.find(p => p?.["custom_attributes"]?.[0]?.value === "member");
  const nonMemberPlan = subscriptionPlans?.find(p => p?.["custom_attributes"]?.[0]?.value === "non-member");

  const PRICING = {
    imaMembers: memberPlan?.price,
    nonMembers: nonMemberPlan?.price,
  };

  const [errors, setErrors] = useState({});
  const [showPartnerFields, setShowPartnerFields] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!validateMobile(formData.mobile)) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number";
    }
    if (!formData.company.trim()) newErrors.company = "Company is required";
    if (!formData.designation.trim()) newErrors.designation = "Designation is required";
    if (!formData.address.trim()) newErrors.address = "Address is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.pincode.trim()) newErrors.pincode = "Pincode is required";
    if (!formData.gst.trim()) newErrors.gst = "GST number is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addPartner = () => {
    setShowPartnerFields(true);
    setFormData(prev => ({
      ...prev,
      partners: [...prev.partners, { name: "", email: "", mobile: "", company: "", designation: "" }]
    }));
  };

  const updatePartner = (index, field, value) => {
    const updatedPartners = [...formData.partners];
    updatedPartners[index] = { ...updatedPartners[index], [field]: value };
    setFormData(prev => ({ ...prev, partners: updatedPartners }));
  };

  const removePartner = () => {
    setShowPartnerFields(false);
    setFormData(prev => ({ ...prev, partners: [] }));
  };

  const sendSeatBookingEmail = async (selectedPlan, clientManagerEmail) => {
    try {
      // Prepare attendee data from form
      const attendeeData = {
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile,
        designation: formData.designation,
        company: formData.company,
        address: formData.address,
        city: formData.city,
        pincode: formData.pincode,
        gst: formData.gst,
        po: formData.po,
        memberType: isActiveMember ? "IMA Member" : "Non Member",
        partners: formData.partners
      };

      // Prepare payment data
      const paymentData = {
        paymentAmount: selectedPlan?.price || (isActiveMember ? PRICING.imaMembers : PRICING.nonMembers)
      };

      const emailData = {
        eventName: story?.headline,
        eventUrl: story?.url,
        attendeeData,
        paymentData,
        clientManagerEmail,
      };

      const response = await fetch("/seat-booking-confirmation-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(emailData),
      });

      if (!response.ok) {
        console.error("Failed to send seat booking confirmation email");
      }
    } catch (error) {
      console.error("Error sending seat booking confirmation email:", error);
    }
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      // Here you would integrate with payment gateway
      const memberPlan = subscriptionPlans?.find(p => p?.["custom_attributes"]?.[0]?.value === "member");
      const nonMemberPlan = subscriptionPlans?.find(p => p?.["custom_attributes"]?.[0]?.value === "non-member");
      const clientManagerEmail = get(subscriberMetadata, ["client-manager-email"], "");
      const selectedPlan = isActiveMember ? memberPlan : nonMemberPlan;
      try {
        const paymentResponse = await initRazorPayPayment(
          selectedPlan,
          "standard"
        );

        if (paymentResponse.subscription) {
          // Send seat booking confirmation email
          await sendSeatBookingEmail(selectedPlan, clientManagerEmail);
          alert("Thank you! you have successfully booked a seat for this event");

          // Reload the page to show full content
          window.location.reload();
        }
      } catch (err) {
        if (err?.error?.code === "subscription_preview_active_subscription") {
          setError("You already have an active subscription for this asset.");
        } else {
          setError(err.message || "Payment failed. Please try again.");
        }
      } 
    }
  };

  const isFormValid = () => {
    const requiredFields = ['name', 'email', 'mobile', 'company', 'designation', 'address', 'city', 'pincode', 'gst', 'po'];
    const hasAllRequiredFields = requiredFields.every(field => formData[field].trim());
    const hasValidEmail = validateEmail(formData.email);
    const hasValidMobile = validateMobile(formData.mobile);
    const isUserSignedIn = member !== null;
    const hasAcceptedPrivacy = formData.privacyConsent === true;

    // If partner fields are shown, check that all partner fields are filled
    let hasValidPartnerFields = true;
    if (showPartnerFields && formData.partners.length > 0) {
      hasValidPartnerFields = formData.partners.every(partner =>
        partner.name.trim() &&
        partner.email.trim() &&
        partner.mobile.trim() &&
        validateEmail(partner.email) &&
        validateMobile(partner.mobile)
      );
    }

    return hasAllRequiredFields && hasValidEmail && hasValidMobile && isUserSignedIn && hasAcceptedPrivacy && hasValidPartnerFields;
  };

  return (
    <>
      <div styleName="book-seat-section" className={className} data-section="book-seat">
        <div styleName="section-content">
          {/* Left Side - Pricing */}
          <div styleName="pricing-section">
          <div styleName="pricing-header">
            <h4 styleName="pricing-label">PRICING</h4>
          </div>
          <div styleName="pricing-container">
            <div styleName="price-item">
              <div styleName="member-type">IMA Forum Members</div>
              <div styleName="price-details">
                <span styleName="price">{PRICING?.imaMembers?.toLocaleString()}</span>
                <span styleName="per-participant">/ per participant</span>
              </div>
            </div>
            <div styleName="price-item">
              <div styleName="member-type">Non-Members</div>
              <div styleName="price-details">
                <span styleName="price">{PRICING?.nonMembers?.toLocaleString()}</span>
                <span styleName="per-participant">/ per participant</span>
              </div>
            </div>
          </div>
          <div styleName="pricing-notes">
            <div styleName="note">* GST applicable</div>
            <div styleName="note">* Preferential fee for IMA Forum members and nominees</div>
          </div>
          {!member && (
            <div styleName="sign-in-section">
              <button styleName="sign-in-link" onClick={onSignIn}>
                Sign in
              </button>
              <span styleName="sign-in-text">to get member pricing</span>
            </div>
          )}
        </div>

        {/* Right Side - Form */}
        <div styleName="form-section">
          <div styleName="form-header">
            <h3 styleName="form-title">Book a seat now</h3>
          </div>

          <div styleName="form-content">
            <div styleName="section-title">Your Details</div>

            {/* Name Field */}
            <div styleName="form-group">
              <div styleName={`input-field ${errors.name ? 'error' : ''}`}>
                <InputField
                  placeholder="Name*"
                  value={formData.name}
                  label=""
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              </div>
              {errors.name && <div styleName="error-message">{errors.name}</div>}
            </div>

            {/* Email and Mobile Row */}
            <div styleName="form-row">
              <div styleName="form-group half-width">
                <div styleName={`input-field ${errors.email ? 'error' : ''}`}>
                  <InputField
                    type="email"
                    placeholder="Email*"
                    value={formData.email}
                    label=""
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </div>
                {errors.email && <div styleName="error-message">{errors.email}</div>}
              </div>
              <div styleName="form-group half-width">
                <div styleName={`input-field ${errors.mobile ? 'error' : ''}`}>
                  <InputField
                    type="tel"
                    placeholder="Mobile*"
                    value={formData.mobile}
                    label=""
                    onChange={(e) => handleInputChange('mobile', e.target.value)}
                  />
                </div>
                {errors.mobile && <div styleName="error-message">{errors.mobile}</div>}
              </div>
            </div>

            {/* Company and Designation Row */}
            <div styleName="form-row">
              <div styleName="form-group half-width">
                <div styleName={`input-field ${errors.company ? 'error' : ''}`}>
                  <InputField
                    placeholder="Company*"
                    value={formData.company}
                    label=""
                    onChange={(e) => handleInputChange('company', e.target.value)}
                  />
                </div>
                {errors.company && <div styleName="error-message">{errors.company}</div>}
              </div>
              <div styleName="form-group half-width">
                <div styleName={`input-field ${errors.designation ? 'error' : ''}`}>
                  <InputField
                    placeholder="Designation*"
                    value={formData.designation}
                    label=""
                    onChange={(e) => handleInputChange('designation', e.target.value)}
                  />
                </div>
                {errors.designation && <div styleName="error-message">{errors.designation}</div>}
              </div>
            </div>

            {/* Address Field */}
            <div styleName="form-group">
              <div styleName={`input-field ${errors.address ? 'error' : ''}`}>
                <InputField
                  placeholder="Address*"
                  value={formData.address}
                  label=""
                  onChange={(e) => handleInputChange('address', e.target.value)}
                />
              </div>
              {errors.address && <div styleName="error-message">{errors.address}</div>}
            </div>

            {/* City and Pincode Row */}
            <div styleName="form-row">
              <div styleName="form-group half-width">
                <div styleName={`input-field ${errors.city ? 'error' : ''}`}>
                  <InputField
                    placeholder="City*"
                    value={formData.city}
                    label=""
                    onChange={(e) => handleInputChange('city', e.target.value)}
                  />
                </div>
                {errors.city && <div styleName="error-message">{errors.city}</div>}
              </div>
              <div styleName="form-group half-width">
                <div styleName={`input-field ${errors.pincode ? 'error' : ''}`}>
                  <InputField
                    placeholder="Pincode*"
                    value={formData.pincode}
                    label=""
                    onChange={(e) => handleInputChange('pincode', e.target.value)}
                  />
                </div>
                {errors.pincode && <div styleName="error-message">{errors.pincode}</div>}
              </div>
            </div>

            {/* GST Field */}
            <div styleName="form-group">
              <div styleName={`input-field ${errors.gst ? 'error' : ''}`}>
                <InputField
                  placeholder="Company GST (billing GST)*"
                  value={formData.gst}
                  label=""
                  onChange={(e) => handleInputChange('gst', e.target.value)}
                />
              </div>
              {errors.gst && <div styleName="error-message">{errors.gst}</div>}
            </div>

            {/* PO Radio Buttons */}
            <div styleName="form-group">
              <div styleName="radio-group">
                <span styleName="radio-label">PO*</span>
                <div styleName="radio-options">
                  <label styleName="radio-option">
                    <input
                      type="radio"
                      name="po"
                      value="Yes"
                      checked={formData.po === "Yes"}
                      onChange={(e) => handleInputChange('po', e.target.value)}
                    />
                    <span styleName="radio-text">Yes</span>
                  </label>
                  <label styleName="radio-option">
                    <input
                      type="radio"
                      name="po"
                      value="No"
                      checked={formData.po === "No"}
                      onChange={(e) => handleInputChange('po', e.target.value)}
                    />
                    <span styleName="radio-text">No</span>
                  </label>
                </div>
              </div>
              <div styleName="po-note">
                If yes, please send a copy to <span styleName="email-link">accounts@ima-india.com</span>
              </div>
            </div>

            {/* Privacy Consent */}
            <div styleName="form-group checkbox-field">
              <label styleName="checkbox-group">
                <input
                  type="checkbox"
                  checked={formData.privacyConsent}
                  onChange={(e) => handleInputChange('privacyConsent', e.target.checked)}
                />
                <span styleName="checkbox-text">
                  By filling and submitting this form, you consent to IMA India and its select partner companies processing your personal information in accordance with their <strong>Privacy Policy</strong>, and further, you consent to your information being shared with fellow attendees and roundtable partners for conference-related purposes.
                </span>
              </label>
            </div>

            {/* Add Partner Section */}
            {!showPartnerFields && <div styleName="add-partner-section">
              <button
                styleName="add-partner-button"
                onClick={addPartner}
                type="button"
              >
                <PlusIcon size="10px" strokeWidth="1.25" />
                Add Partner
              </button>
            </div>}

            {/* Partner Fields (when shown) */}
            {showPartnerFields && formData.partners.map((partner, index) => (
              <div key={index} styleName="partner-section">
                <div styleName="partner-header">
                  <h4 styleName="partner-title">Partner Details</h4>
                  <button
                    styleName="partner-close-button"
                    onClick={removePartner}
                    type="button"
                    aria-label="Remove partner"
                  >
                    <CloseIcon />
                  </button>
                </div>

                {/* Partner Name Field */}
                <div styleName="form-group">
                  <div styleName="input-field">
                    <InputField
                      placeholder="Name*"
                      value={partner.name}
                      label=""
                      onChange={(e) => updatePartner(index, 'name', e.target.value)}
                    />
                  </div>
                </div>

                {/* Partner Email and Mobile Row */}
                <div styleName="form-row">
                  <div styleName="form-group half-width">
                    <div styleName="input-field">
                      <InputField
                        type="email"
                        placeholder="Email*"
                        value={partner.email}
                        label=""
                        onChange={(e) => updatePartner(index, 'email', e.target.value)}
                      />
                    </div>
                  </div>
                  <div styleName="form-group half-width">
                    <div styleName="input-field">
                      <InputField
                        type="tel"
                        placeholder="Mobile*"
                        value={partner.mobile}
                        label=""
                        onChange={(e) => updatePartner(index, 'mobile', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Invoice Note */}
            <div styleName="invoice-note">
              * The invoice shall be issued basis the details given above. No further amendments shall be accommodated.
            </div>

            {/* Submit Button */}
            <button
              styleName={`submit-button ${isFormValid() ? 'enabled' : ''}`}
              onClick={handleSubmit}
              disabled={!isFormValid()}
            >
              Pay {isActiveMember ? (PRICING.imaMembers)?.toLocaleString()?.replace('₹ ', '₹') : (PRICING.nonMembers)?.toLocaleString()?.replace('₹ ', '₹')}
            </button>
          </div>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div styleName="help-section">
        <div styleName="help-content">
          <h4 styleName="help-title">Need help with booking a seat?</h4>
          <div styleName="help-details">
            <p styleName="help-description">
              Share your <span styleName="highlight">details here</span>, or contact us
            </p>
            <div styleName="contact-info">
              {homePageData?.contactEmail && <div styleName="contact-item">
                <strong>Email</strong> {homePageData?.contactEmail}
              </div>}
              {homePageData?.contactPhone && <div styleName="contact-item">
                <strong>Phone</strong> {homePageData?.contactPhone}
              </div>}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

BookSeatSection.propTypes = {
  subscriptionPlans: object,
  onSignIn: func,
  className: string,
  initRazorPayPayment: func,
  story: object,
};